/**
 * ConfirmDialog — confirmação na tela, no lugar do `window.confirm`.
 *
 * Props:
 * - open, titulo, descricao (string | ReactNode)
 * - confirmarLabel (default "Confirmar"), cancelarLabel (default "Cancelar")
 * - tone: "danger" (padrão para exclusão) | "primary"
 * - onConfirmar(): pode ser async — o botão mostra o spinner e trava até
 *   resolver; erro é do chamador (toast) e o diálogo continua aberto.
 * - onCancelar()
 */
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiHelpCircle } from "react-icons/fi";

import { MiniSpinner } from "../LoadingMarcenaria";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Caixa = styled.div`
  width: min(92vw, 440px);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  padding: 22px 22px 18px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Cabeca = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;

  .icone {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: ${({ theme, $tone }) => ($tone === "danger" ? theme.danger : theme.accent)};
    position: relative;
  }
  .icone::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.12;
  }
  h3 {
    margin: 0 0 4px;
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => theme.textPrimary};
  }
  p {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

const Botoes = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
`;

const Botao = styled.button`
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ theme, $tone }) =>
    $tone === "danger" ? theme.danger : $tone === "primary" ? theme.accent : theme.border};
  background: ${({ theme, $tone }) =>
    $tone === "danger" ? theme.danger : $tone === "primary" ? theme.accent : "transparent"};
  color: ${({ theme, $tone }) => ($tone ? "#fff" : theme.textPrimary)};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function ConfirmDialog({
  open,
  titulo,
  descricao,
  confirmarLabel = "Confirmar",
  cancelarLabel = "Cancelar",
  tone = "danger",
  onConfirmar,
  onCancelar,
}) {
  const [executando, setExecutando] = useState(false);
  const cancelarRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setExecutando(false);
    cancelarRef.current?.focus();
    const aoTeclar = (e) => {
      if (e.key === "Escape" && !executando) onCancelar?.();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [open, executando, onCancelar]);

  if (!open) return null;

  const confirmar = async () => {
    setExecutando(true);
    try {
      await onConfirmar?.();
    } finally {
      setExecutando(false);
    }
  };

  const Icone = tone === "danger" ? FiAlertTriangle : FiHelpCircle;
  return (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget && !executando) onCancelar?.(); }}>
      <Caixa role="alertdialog" aria-modal="true" aria-labelledby="confirm-titulo">
        <Cabeca $tone={tone}>
          <span className="icone"><Icone size={20} /></span>
          <div>
            <h3 id="confirm-titulo">{titulo}</h3>
            {descricao ? <p>{descricao}</p> : null}
          </div>
        </Cabeca>
        <Botoes>
          <Botao type="button" ref={cancelarRef} onClick={onCancelar} disabled={executando}>{cancelarLabel}</Botao>
          <Botao type="button" $tone={tone} onClick={confirmar} disabled={executando}>
            {executando ? <MiniSpinner size={13} label="Aguarde" /> : null}
            {confirmarLabel}
          </Botao>
        </Botoes>
      </Caixa>
    </Overlay>
  );
}
